import { useEffect, useRef, useState } from 'react';
import { CONTEST_DOC_TYPES } from '@isbd/shared';
import { supabase } from '@/lib/supabase';
import { deleteContestDocument, signContestDocumentUrl, uploadContestDocument } from '@/lib/bunnyStorage';
import { useAuth } from '@/features/auth/AuthProvider';
import { useToast } from '@/features/ui/toast/useToast';

interface DocRow {
  id: string;
  doc_type: string;
  storage_path: string;
  created_at: string;
}

interface Props {
  registrationId: string;
  readOnly?: boolean;
}

export function ContestDocumentsPanel({ registrationId, readOnly = false }: Props) {
  const { profile } = useAuth();
  const toast = useToast();
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [uploading, setUploading] = useState<string | null>(null);
  const inputRefs = useRef<Record<string, HTMLInputElement | null>>({});

  async function load() {
    const { data, error: err } = await supabase
      .from('contest_documents')
      .select('id, doc_type, storage_path, created_at')
      .eq('registration_id', registrationId)
      .order('created_at', { ascending: false });
    if (err) toast.error(err.message);
    else setDocs((data ?? []) as DocRow[]);
  }

  useEffect(() => {
    void load();
  }, [registrationId]);

  async function upload(docType: string, file: File) {
    if (!profile) return;
    setUploading(docType);
    try {
      const safeName = file.name.replace(/[^\w.\-]+/g, '_');
      const path = `${profile.id}/${registrationId}/${docType}_${Date.now()}_${safeName}`;

      await uploadContestDocument({
        registrationId,
        storagePath: path,
        file,
      });

      const existing = docs.find((d) => d.doc_type === docType);
      if (existing) {
        await supabase.from('contest_documents').delete().eq('id', existing.id);
        try {
          await deleteContestDocument({ registrationId, storagePath: existing.storage_path });
        } catch {
          // Ancien fichier éventuellement encore sur Supabase Storage — on ignore
          await supabase.storage.from('contest-documents').remove([existing.storage_path]);
        }
      }

      const { error: dbErr } = await supabase.from('contest_documents').insert({
        registration_id: registrationId,
        doc_type: docType,
        storage_path: path,
      });
      if (dbErr) throw dbErr;
      toast.success(`Document « ${docType} » envoyé.`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Upload impossible');
    } finally {
      setUploading(null);
    }
  }

  async function openDoc(storagePath: string) {
    try {
      const signedUrl = await signContestDocumentUrl({ registrationId, storagePath });
      window.open(signedUrl, '_blank', 'noopener,noreferrer');
    } catch {
      // Compat docs déjà déposés sur Supabase Storage
      const { data, error: err } = await supabase.storage.from('contest-documents').createSignedUrl(storagePath, 3600);
      if (err) toast.error(err.message);
      else if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener,noreferrer');
    }
  }

  return (
    <div style={{ marginTop: '1rem' }}>
      <h4>Documents de candidature</h4>
      <ul style={{ listStyle: 'none', padding: 0, display: 'grid', gap: '0.75rem' }}>
        {CONTEST_DOC_TYPES.map((t) => {
          const existing = docs.find((d) => d.doc_type === t.key);
          return (
            <li key={t.key} className="panel" style={{ padding: '0.75rem 1rem', margin: 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.75rem', flexWrap: 'wrap' }}>
                <div>
                  <strong>
                    {t.label}
                    {t.required ? ' *' : ''}
                  </strong>
                  <p className="muted" style={{ margin: '0.2rem 0 0' }}>
                    {existing ? `Déposé le ${new Date(existing.created_at).toLocaleDateString('fr-FR')}` : 'Non déposé'}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.35rem', alignItems: 'center' }}>
                  {existing ? (
                    <button type="button" className="btn btn-ghost" onClick={() => void openDoc(existing.storage_path)}>
                      Voir
                    </button>
                  ) : null}
                  {!readOnly ? (
                    <>
                      <input
                        ref={(el) => {
                          inputRefs.current[t.key] = el;
                        }}
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.mp4,.mov"
                        onChange={(e) => {
                          const f = e.target.files?.[0];
                          if (f) void upload(t.key, f);
                          e.target.value = '';
                        }}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={uploading === t.key}
                        onClick={() => inputRefs.current[t.key]?.click()}
                      >
                        {uploading === t.key ? 'Envoi…' : existing ? 'Remplacer' : 'Déposer'}
                      </button>
                    </>
                  ) : null}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
